import { Report } from "@/services/backend/htz/report/types/report";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { LaboratoriesCard } from "@/routes/processes/[processId]/htz/summaries/[summaryId]/items/[itemId]/report/data/_components/laboratories-card";
import { AhuDataBaseDataCard } from "@/services/backend/htz/ahu/components/ahu-data-base-data-card";
import { ReportBaseDataCard } from "@/services/backend/htz/ahu/components/report-base-data-card";
import { AddressCard } from "@/services/backend/addresses/components/address-card";
import { AssignAhuForReportDialog } from "@/services/backend/htz/report/components/assign-ahu-for-report-dialog";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  updateHtzReportCache,
  PatchRequest,
  useHtzReportPatchMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";

export function ReportData({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });
  const request: PatchRequest = { id: report.id };
  // Sharing set across the components could lead
  // to a loss of input if a user extremely quickly
  // jumps from one component to the other and starts
  // input. This is just unlikely.
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMutation(),
  );

  // just to avoid type errors
  if (!data) return;

  return (
    <div className="space-y-2">
      <ReportBaseDataCard id={report.id} disabled={isImmutable(report)} />
      {report.ahuDataId ? (
        <AhuDataBaseDataCard
          id={report.ahuDataId}
          hideLabel
          disabled={isImmutable(report)}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t("RLT-Anlage")}</CardTitle>
            <CardDescription>
              {t("Dem Anlagenbericht ist noch keine RLT-Anlage zugewiesen.")}
            </CardDescription>
            {!isImmutable(report) && (
              <CardAction>
                <AssignAhuForReportDialog report={report} />
              </CardAction>
            )}
          </CardHeader>
        </Card>
      )}
      <div className="grid grid-cols-2 gap-2">
        <LaboratoriesCard report={report} />
        <AddressCard
          title={t("Erstellt durch")}
          description={t("Adresse der Niederlassung, die den Bericht erstellt")}
          address={data.createdByCompany}
          onAddressChange={(createdByCompany) =>
            set({ ...request, createdByCompany }, { ...data, createdByCompany })
          }
          fieldName="createdByCompany"
          disabled={isImmutable(report)}
        />
        <AddressCard
          title={t("Objektadresse")}
          description={t("Adresse des Objekts der Inspektion")}
          address={data.objectAddress}
          onAddressChange={(objectAddress) =>
            set({ ...request, objectAddress }, { ...data, objectAddress })
          }
          fieldName="objectAddress"
          disabled={isImmutable(report)}
        />
        <AddressCard
          title={t("Erstellt für")}
          description={t("Adresse des Empfängers des Berichts")}
          address={data.createdFor}
          onAddressChange={(createdFor) =>
            set({ ...request, createdFor }, { ...data, createdFor })
          }
          fieldName="createdFor"
          disabled={isImmutable(report)}
        />
      </div>
    </div>
  );
}

export function ReportDataSkeleton() {
  return <CardSkeleton />;
}
